import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Mail, Send, AlertTriangle, CheckCircle2, Eye, Search, RefreshCw, Paperclip, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
    });
  } catch {
    return iso;
  }
}

export default function ColorWarRevealEmailPanel() {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [resendQuery, setResendQuery] = useState("");
  const [resendingId, setResendingId] = useState(null);
  const [blueName, setBlueName] = useState("");
  const [whiteName, setWhiteName] = useState("");
  const [savingNames, setSavingNames] = useState(false);
  const [pdfInfo, setPdfInfo] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const loadPdfStatus = async () => {
    try {
      const r = await axios.get(`${API}/color-war/reveal-email/pdf/status`, { headers });
      setPdfInfo(r.data.attached ? r.data : null);
    } catch {}
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPdfUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/color-war/reveal-email/pdf`, fd, { headers });
      toast.success(r.data.message);
      loadPdfStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "PDF upload failed");
    }
    setPdfUploading(false);
    e.target.value = "";
  };

  const handlePdfRemove = async () => {
    try {
      await axios.delete(`${API}/color-war/reveal-email/pdf`, { headers });
      toast.success("PDF attachment removed");
      setPdfInfo(null);
    } catch { toast.error("Failed to remove PDF"); }
  };

  const loadTeamNames = async () => {
    try {
      const r = await axios.get(`${API}/color-war/team-names`, { headers });
      setBlueName(r.data.blue || "");
      setWhiteName(r.data.white || "");
    } catch {}
  };

  useEffect(() => { loadTeamNames(); loadPdfStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveTeamNames = async () => {
    setSavingNames(true);
    try {
      await axios.put(`${API}/color-war/team-names`, { blue: blueName.trim(), white: whiteName.trim() }, { headers });
      toast.success("Team names saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingNames(false);
    }
  };

  const loadPreview = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/color-war/reveal-email/preview`, { headers });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load preview");
    } finally {
      setLoading(false);
    }
  };

  const resendMatches = useMemo(() => {
    const list = preview?.recipients || [];
    const q = resendQuery.trim().toLowerCase();
    if (!q) return [];
    return list
      .filter((r) => {
        const name = (r.camper_name || "").toLowerCase();
        const email = (r.parent_email || "").toLowerCase();
        const code = (r.group_code || "").toLowerCase();
        return name.includes(q) || email.includes(q) || code.includes(q);
      })
      .slice(0, 8);
  }, [preview, resendQuery]);

  const handleResendOne = async (recipient) => {
    setResendingId(recipient.camper_id);
    try {
      const r = await axios.post(
        `${API}/color-war/reveal-email/resend`,
        { camper_id: recipient.camper_id },
        { headers },
      );
      toast.success(`Resent to ${r.data.parent_email} (${recipient.camper_name} · ${r.data.team.toUpperCase()})`);
      setResendQuery("");
      await loadPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resend");
    } finally {
      setResendingId(null);
    }
  };

  // Auto-load once on mount so the resend search works immediately
  useEffect(() => { loadPreview(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openSendDialog = async () => {
    await loadPreview();
    setDialogOpen(true);
  };

  const openPreviewEmail = async () => {
    if (!preview) await loadPreview();
    setPreviewDialogOpen(true);
  };

  const pollJob = async (jobId) => {
    for (;;) {
      await new Promise((res) => setTimeout(res, 2000));
      try {
        const r = await axios.get(`${API}/color-war/email-job/${jobId}`, { headers });
        setProgress(r.data);
        if (r.data.status !== "running") return r.data;
      } catch {}
    }
  };

  const handleSend = async (force = false) => {
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/color-war/reveal-email/send`,
        { confirm: true, force },
        { headers },
      );
      setDialogOpen(false);
      setResult(null);
      setProgress({ status: "running", sent_ok: 0, failed: 0, total: r.data.recipient_count });
      toast.info(`Sending ${r.data.recipient_count} emails in the background — you can leave this page.`);
      const final = await pollJob(r.data.job_id);
      setProgress(null);
      setResult(final);
      toast.success(`Sent ${final.sent_ok} emails — ${final.failed} failed`);
      await loadPreview();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || "Failed to send";
      if (status === 409 && !detail.includes("in progress")) {
        if (window.confirm(`${detail}\n\nSend anyway? (This will double-email every parent.)`)) {
          setSending(false);
          await handleSend(true);
          return;
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setSending(false);
    }
  };

  const lastSend = preview?.history?.[0];

  return (
    <div className="bg-gradient-to-br from-blue-50 to-slate-50 border-2 border-blue-300 rounded-xl p-4" data-testid="cw-reveal-email-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center flex-shrink-0">
            <Mail className="w-5 h-5 text-blue-800" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Color War Break — Email Parents</h3>
            <p className="text-sm text-gray-600 mt-0.5">
              Sends each parent: <em>"Color war has broken! Your child {"{name}"} is on {"{COLOR}"} — {"{Team Name}"}. Let's get ready for an awesome Color War week!"</em>
              Only eligible campers (Aug 10–14, 2026) are included.
            </p>
            {lastSend && (
              <p className="text-xs text-gray-500 mt-2">
                Last sent: <strong>{fmtDateTime(lastSend.sent_at)}</strong> — {lastSend.sent_ok} sent, {lastSend.failed} failed
              </p>
            )}
            <div className="mt-3 flex flex-wrap items-end gap-2 p-2 rounded-lg bg-white border border-blue-200">
              <div className="flex flex-col">
                <label className="text-[10px] font-bold text-blue-800 uppercase tracking-wide">Blue Team Name</label>
                <Input
                  value={blueName}
                  onChange={(e) => setBlueName(e.target.value)}
                  placeholder="e.g. Tidal Wave"
                  className="h-8 w-44 text-sm"
                  data-testid="cw-blue-team-name"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-[10px] font-bold text-gray-700 uppercase tracking-wide">White Team Name</label>
                <Input
                  value={whiteName}
                  onChange={(e) => setWhiteName(e.target.value)}
                  placeholder="e.g. Avalanche"
                  className="h-8 w-44 text-sm"
                  data-testid="cw-white-team-name"
                />
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={saveTeamNames}
                disabled={savingNames}
                className="h-8 text-xs"
                data-testid="cw-team-names-save"
              >
                {savingNames ? "Saving…" : "Save Names"}
              </Button>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 p-2 rounded-lg bg-white border border-blue-200" data-testid="cw-reveal-pdf-bar">
              <Paperclip className="w-4 h-4 text-blue-700" />
              {pdfInfo ? (
                <>
                  <span className="text-xs font-bold text-emerald-700" data-testid="cw-reveal-pdf-attached">
                    Attached to every parent email: {pdfInfo.filename} ({Math.round((pdfInfo.size || 0) / 1024)} KB)
                  </span>
                  <button onClick={handlePdfRemove} className="flex items-center gap-1 h-7 px-2 text-xs font-bold text-red-600 border border-red-200 rounded-md hover:bg-red-50" data-testid="cw-reveal-pdf-remove">
                    <X className="w-3 h-3" /> Remove
                  </button>
                </>
              ) : (
                <>
                  <span className="text-xs font-semibold text-gray-700">Attach a PDF to the parent email (optional):</span>
                  <label className="inline-flex items-center h-7 px-2.5 text-xs font-bold border border-blue-300 text-blue-700 rounded-md cursor-pointer hover:bg-blue-50" data-testid="cw-reveal-pdf-upload">
                    {pdfUploading ? "Uploading…" : "Upload PDF"}
                    <input type="file" accept=".pdf,application/pdf" className="hidden" onChange={handlePdfUpload} disabled={pdfUploading} />
                  </label>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={openPreviewEmail} disabled={loading} data-testid="cw-email-preview-btn">
            <Eye className="w-3.5 h-3.5 mr-1" /> Preview email
          </Button>
          <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-white" onClick={openSendDialog} disabled={loading || sending} data-testid="cw-email-send-btn">
            <Send className="w-3.5 h-3.5 mr-1" /> Send Color War Break Email
          </Button>
        </div>
      </div>

      {progress && (
        <div className="mt-4 p-3 bg-white rounded-lg border border-blue-300" data-testid="cw-email-progress">
          <div className="flex items-center gap-2 text-sm font-bold text-blue-800 mb-2">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Sending in background — {(progress.sent_ok || 0) + (progress.failed || 0)} of {progress.total} processed
          </div>
          <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 rounded-full transition-all"
              style={{ width: `${Math.min(100, Math.round((((progress.sent_ok || 0) + (progress.failed || 0)) / (progress.total || 1)) * 100))}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Emails keep sending on the server even if you close this page.</p>
        </div>
      )}

      {result && (
        <div className="mt-4 p-3 bg-white rounded-lg border border-emerald-300" data-testid="cw-email-result">
          <div className="flex items-center gap-2 text-sm font-bold text-emerald-800">
            <CheckCircle2 className="w-4 h-4" />
            Sent — {result.sent_ok} emails delivered, {result.failed} failed
          </div>
        </div>
      )}

      {/* ── Resend to a single camper's parent ── */}
      {(preview?.recipients?.length ?? 0) > 0 && (
        <div className="mt-4 bg-white rounded-lg border border-blue-200 p-3" data-testid="cw-resend-panel">
          <div className="flex items-center gap-2 mb-2">
            <Search className="w-4 h-4 text-blue-700" />
            <p className="text-sm font-bold text-gray-900">Resend to a specific camper's parent</p>
          </div>
          <p className="text-xs text-gray-500 mb-2">
            Use this if a parent says they didn't get the email or it went to spam. Type a camper name, group code (e.g. B01), or parent email.
          </p>
          <Input
            type="text"
            placeholder="Search by camper name, group, or parent email…"
            value={resendQuery}
            onChange={(e) => setResendQuery(e.target.value)}
            className="text-sm"
            data-testid="cw-resend-search"
          />
          {resendQuery.trim() && (
            <div className="mt-2 space-y-1 max-h-72 overflow-y-auto" data-testid="cw-resend-results">
              {resendMatches.length === 0 ? (
                <div className="text-xs text-gray-400 italic p-2">No matches. Only eligible campers (attending Aug 10–14) appear here.</div>
              ) : (
                resendMatches.map((r) => (
                  <div
                    key={r.camper_id}
                    className="flex items-center justify-between gap-3 p-2 rounded border border-gray-200 bg-slate-50 hover:border-blue-400 transition-colors"
                    data-testid={`cw-resend-row-${r.camper_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-gray-900 truncate">{r.camper_name}</span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${r.team === "blue" ? "bg-blue-100 text-blue-800" : "bg-gray-200 text-gray-800"}`}>
                          {r.team.toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-500">{r.group_code}</span>
                      </div>
                      <div className="text-xs text-gray-600 truncate">{r.parent_email}</div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs flex-shrink-0 border-blue-400 text-blue-700 hover:bg-blue-50"
                      disabled={resendingId === r.camper_id}
                      onClick={() => handleResendOne(r)}
                      data-testid={`cw-resend-btn-${r.camper_id}`}
                    >
                      {resendingId === r.camper_id ? (
                        <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Resending…</>
                      ) : (
                        <><Send className="w-3 h-3 mr-1" /> Resend</>
                      )}
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Confirmation dialog ── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="cw-email-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Send Color War Reveal Emails?
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Emails to send</p>
                  <p className="text-2xl font-bold text-gray-900" data-testid="cw-preview-recipient-count">{preview.recipient_count}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Unique parent emails</p>
                  <p className="text-2xl font-bold text-gray-900">{preview.unique_parent_emails}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-blue-700 uppercase tracking-wider">Blue team</p>
                  <p className="text-2xl font-bold text-blue-900">{preview.blue_count}</p>
                </div>
                <div className="bg-slate-100 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-700 uppercase tracking-wider">White team</p>
                  <p className="text-2xl font-bold text-gray-900">{preview.white_count}</p>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                Reply-to: <strong>{preview.reply_to}</strong>. Only campers enrolled for Aug 10–14 receive the email — this excludes HS1 and Flex campers with no days in that window.
              </p>
              {lastSend && (
                <div className="bg-amber-50 border-l-4 border-amber-500 p-3 text-sm text-amber-900 rounded-r">
                  ⚠️ Already sent once on {fmtDateTime(lastSend.sent_at)}. Resending will double-email every parent.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={sending} data-testid="cw-email-cancel-btn">
              Cancel
            </Button>
            <Button
              className="bg-blue-700 hover:bg-blue-800 text-white"
              onClick={() => handleSend(false)}
              disabled={sending || !preview?.recipient_count}
              data-testid="cw-email-confirm-send-btn"
            >
              {sending ? "Sending…" : `Send ${preview?.recipient_count || 0} emails`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Sample email preview dialog ── */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="cw-email-sample-dialog">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
          </DialogHeader>
          {preview?.sample ? (
            <div className="space-y-2 overflow-y-auto">
              <div className="text-xs text-gray-500 bg-slate-50 rounded p-2">
                <div><strong>To:</strong> {preview.sample.to}</div>
                <div><strong>Camper:</strong> {preview.sample.camper}</div>
                <div><strong>Team:</strong> {preview.sample.team}</div>
                <div><strong>Reply-to:</strong> {preview.reply_to}</div>
              </div>
              <iframe
                title="Color War email preview"
                srcDoc={preview.sample.html}
                className="w-full h-[500px] rounded border"
                sandbox=""
              />
            </div>
          ) : (
            <div className="text-center text-sm text-gray-500 py-8">No sample available — no eligible campers yet.</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
